import type { ReactNode } from 'react';
import { CopySimpleIcon, DownloadSimpleIcon, ShareNetworkIcon } from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@voll-intelligence/ui-kit/ui/dropdown-menu';

export type ResultsShareCopy = {
  /** O nome do botão ("Compartilhar a busca"). */
  label: string;
  copyLinkLabel: string;
  downloadPdfLabel: string;
};

/** O compartilhar de uma busca, em dois sabores: só o link (o gesto de antes
 *  da #5156) ou o menu com o PDF. */
export type ResultsShare =
  | { copy: Pick<ResultsShareCopy, 'label'>; onCopyLink: () => void; onDownloadPdf?: undefined }
  | {
      copy: ResultsShareCopy;
      onCopyLink: () => void;
      onDownloadPdf: () => void;
      /** Ainda não há lista para cotar (a espera da 1ª busca): o item fica,
       *  desabilitado, em vez de sumir e mudar o menu de forma. */
      pdfDisabled?: boolean;
    };

type ResultsShareButtonProps = {
  share: ResultsShare;
  /** `ghost` ao lado do título das páginas de resultado; `outline` no rodapé do
   *  card da conversa, ao lado do "Ver todos os hotéis". */
  look?: 'ghost' | 'outline';
};

/**
 * O botão Compartilhar das buscas (#5156). Com `onDownloadPdf`, abre o menu
 * "Copiar link · Baixar PDF" — o mesmo par do menu da viagem; sem ele, copia o
 * link direto, como antes.
 */
export function ResultsShareButton({ share, look = 'ghost' }: ResultsShareButtonProps): ReactNode {
  const button = (
    <Button
      type="button"
      variant={look}
      size={look === 'outline' ? 'icon-lg' : 'icon-sm'}
      aria-label={share.copy.label}
      {...(share.onDownloadPdf ? {} : { onClick: share.onCopyLink })}
    >
      <ShareNetworkIcon aria-hidden="true" />
    </Button>
  );
  if (!share.onDownloadPdf) return button;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={button} />
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={share.onCopyLink}>
          <CopySimpleIcon aria-hidden="true" />
          {share.copy.copyLinkLabel}
        </DropdownMenuItem>
        <DropdownMenuItem disabled={share.pdfDisabled === true} onClick={share.onDownloadPdf}>
          <DownloadSimpleIcon aria-hidden="true" />
          {share.copy.downloadPdfLabel}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** O PDF de uma página de resultado: as legendas do menu e o gesto, que recebe
 *  a lista COMO ESTÁ NA TELA — na ordem e com os filtros aplicados. */
export type ResultsPdfShare<TOffer> = {
  copy: Pick<ResultsShareCopy, 'copyLinkLabel' | 'downloadPdfLabel'>;
  onDownloadPdf: (visible: readonly TOffer[]) => void;
  /** Ver `ResultsShare.pdfDisabled`. */
  pdfDisabled?: boolean;
};

/** O compartilhar da página: nada sem `onShare`, só o link sem `pdfShare`, o
 *  menu com os dois. */
export function resultsShareOf<TOffer>(
  label: string,
  onShare: (() => void) | undefined,
  pdfShare: ResultsPdfShare<TOffer> | undefined,
  visible: readonly TOffer[],
): ResultsShare | undefined {
  if (!onShare) return undefined;
  if (!pdfShare) return { copy: { label }, onCopyLink: onShare };
  return {
    copy: { label, ...pdfShare.copy },
    onCopyLink: onShare,
    onDownloadPdf: () => {
      pdfShare.onDownloadPdf(visible);
    },
    ...(pdfShare.pdfDisabled ? { pdfDisabled: true } : {}),
  };
}

/** O botão no lugar das ações do título das páginas de resultado. */
export function titleShareOf(share: ResultsShare | undefined): { titleActions?: ReactNode } {
  return share ? { titleActions: <ResultsShareButton share={share} /> } : {};
}
